import { ReactNode } from 'react';
import { SellerAutomation, useSellerAutomation } from '../../hooks/useSellerAutomation';
import { OverflowItem, OverflowMenu, useSellerSettings } from './sellerUi';

// ---------------------------------------------------------------------------
// Cabecalho padrao de toda aba operacional do Seller.
//
// Antes cada aba inventava o seu: umas mostravam "ultima execucao", outras nao;
// o botao de rodar tinha cinco nomes diferentes; o feedback saia ora em toast,
// ora em banner local. Aqui a forma e uma so:
//
//   titulo + subtitulo
//   badge por automacao (ligado/desligado, ultima e proxima execucao)
//   acoes manuais a direita  -> uma principal visivel (SellerRunButton ou
//                               CreditConfirmRunButton quando houver cobranca),
//                               o resto vai no "⋯" (prop `more`)
//   botao "Automacoes"       -> abre o painel lateral de configuracao da tela
//   barra de progresso opcional
//   resumo da ultima rodada opcional
// ---------------------------------------------------------------------------

function formatDateTime(value: string | null): string {
  if (!value) return 'nunca';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'nunca';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatRelative(value: string | null): string | null {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  const diffMin = Math.round((target - Date.now()) / 60000);
  if (diffMin <= 0) return 'a qualquer momento';
  if (diffMin < 60) return `em ${diffMin} min`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `em ${hours}h`;
  return `em ${Math.floor(hours / 24)}d`;
}

function AutomationBadge({ automation, onConfigure }: { automation: SellerAutomation; onConfigure?: () => void }) {
  const next = automation.enabled ? formatRelative(automation.nextRunAt) : null;
  return (
    <button
      type="button"
      onClick={onConfigure}
      disabled={!onConfigure}
      title={onConfigure ? 'Configurar esta automação' : undefined}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-600 transition enabled:hover:border-orange-300 enabled:hover:bg-orange-50/50"
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${automation.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
      <span className="truncate font-medium text-gray-700">{automation.label}</span>
      <span className={automation.enabled ? 'text-green-700' : 'text-gray-400'}>{automation.enabled ? 'automático' : 'manual'}</span>
      <span className="hidden text-gray-400 sm:inline">· última {formatDateTime(automation.lastRunAt)}</span>
      {next && <span className="hidden text-gray-400 sm:inline">· próxima {next}</span>}
    </button>
  );
}

// Mesma linguagem visual do badge de automacao acima: chip pequeno, borda leve,
// texto 11px. Botao de acao nao deve competir com o conteudo da aba.
const RUN_BUTTON_BASE =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition disabled:opacity-50';

// Classe compartilhada para que os botoes de credito (CreditConfirmRunButton)
// fiquem visualmente identicos aos botoes comuns do cabecalho.
export type SellerRunButtonVariant = 'primary' | 'secondary' | 'danger';

export const sellerRunButtonClass = (variant: SellerRunButtonVariant = 'secondary') => {
  if (variant === 'primary') return `${RUN_BUTTON_BASE} border-orange-700 bg-orange-700 text-white hover:border-orange-800 hover:bg-orange-800`;
  if (variant === 'danger') return `${RUN_BUTTON_BASE} border-red-200 bg-white text-red-700 hover:bg-red-50`;
  return `${RUN_BUTTON_BASE} border-gray-200 bg-gray-50/70 text-gray-700 hover:bg-gray-100 hover:text-gray-900`;
};

// Selects que vivem na barra de acoes do cabecalho usam a mesma altura e peso
// visual dos botoes, senao a linha fica desalinhada.
export const sellerHeaderSelectClass =
  'shrink-0 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700';

export function SellerRunButton({
  label, runningLabel, running, disabled, onClick, variant = 'secondary', title,
}: {
  label: string;
  runningLabel?: string;
  running?: boolean;
  disabled?: boolean;
  onClick: () => void;
  variant?: SellerRunButtonVariant;
  title?: string;
}) {
  return (
    <button type="button" onClick={onClick} disabled={running || disabled} title={title} className={sellerRunButtonClass(variant)}>
      {running && (
        <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      {running ? (runningLabel || 'Executando...') : label}
    </button>
  );
}

export function SellerAutomationHeader({
  title,
  subtitle,
  automationKeys = [],
  actions,
  progress,
  result,
  more = [],
}: {
  title: string;
  subtitle?: string;
  /** Chaves de sellerAutomationService.getStatus exibidas como badge nesta aba. */
  automationKeys?: string[];
  /** Botoes de execucao manual. Use SellerRunButton para manter o padrao. */
  actions?: ReactNode;
  /** Progresso da rodada em andamento. */
  progress?: { percent?: number | null; label: string } | null;
  /** Resumo textual da ultima rodada. */
  result?: ReactNode;
  /** Acoes secundarias, escondidas no menu "⋯". */
  more?: OverflowItem[];
}) {
  const { byKey } = useSellerAutomation();
  const { openSettings, canConfigure } = useSellerSettings();
  const badges = automationKeys.map(byKey).filter(Boolean) as SellerAutomation[];
  const firstTab = badges[0]?.settingsTab;

  return (
    <div className="mb-5 space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          <OverflowMenu items={more} />
          {canConfigure && (
            <button
              type="button"
              onClick={() => openSettings(firstTab)}
              title="Automações e regras desta tela"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Automações
            </button>
          )}
        </div>
      </div>

      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {badges.map((automation) => (
            <AutomationBadge key={automation.key} automation={automation} onConfigure={canConfigure ? () => openSettings(automation.settingsTab) : undefined} />
          ))}
        </div>
      )}

      {progress && (
        <div className="rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2">
          <div className="flex items-center justify-between text-xs font-medium text-orange-900">
            <span>{progress.label}</span>
            {progress.percent != null && <span>{Math.round(progress.percent)}%</span>}
          </div>
          {progress.percent != null && (
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-orange-200">
              <div className="h-full rounded-full bg-orange-600 transition-all" style={{ width: `${Math.min(100, Math.max(0, progress.percent))}%` }} />
            </div>
          )}
        </div>
      )}

      {result && <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">{result}</div>}
    </div>
  );
}
